import { useState } from 'react';
import { api, auth, fmt, fmtDate } from '../api';
import { useAction, useAsync } from '../hooks';

// ─────────────────────────── Вход ───────────────────────────

export function LoginPage({ onLoggedIn }: { onLoggedIn: () => void }) {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const submit = useAction(async () => {
    const r = await api.login(login, password);
    auth.set(r.token);
    onLoggedIn();
  });

  return (
    <div className="center">
      <form
        className="card"
        style={{ width: 340 }}
        onSubmit={(e) => {
          e.preventDefault();
          void submit.run();
        }}
      >
        <h1 style={{ marginBottom: 16 }}>Админка</h1>
        {submit.error && <div className="notice err">{submit.error}</div>}
        <div className="field">
          <label>Логин</label>
          <input value={login} onChange={(e) => setLogin(e.target.value)} autoComplete="username" />
        </div>
        <div className="field">
          <label>Пароль</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
        </div>
        <button type="submit" disabled={submit.busy || !login || !password} style={{ width: '100%' }}>
          {submit.busy ? 'Входим…' : 'Войти'}
        </button>
      </form>
    </div>
  );
}

// ─────────────────────────── Отчёты (ТЗ §7.6) ───────────────────────────

const iso = (d: Date) => d.toISOString().slice(0, 10);

export function ReportsPage() {
  const [from, setFrom] = useState(iso(new Date(Date.now() - 30 * 86_400_000)));
  const [to, setTo] = useState(iso(new Date()));
  const { data, loading, error } = useAsync(
    () => api.reports(new Date(from).toISOString(), new Date(`${to}T23:59:59`).toISOString()),
    [from, to],
  );

  return (
    <>
      <h1>Отчёты</h1>
      <p className="subtitle">Начислено за период, потрачено, остаток обязательств, покупки по товарам.</p>

      <div className="row" style={{ marginBottom: 16 }}>
        <input type="date" style={{ width: 170 }} value={from} onChange={(e) => setFrom(e.target.value)} />
        <input type="date" style={{ width: 170 }} value={to} onChange={(e) => setTo(e.target.value)} />
        <a
          className="badge"
          href={`/api/admin/reports/export.csv?kind=purchases&from=${new Date(from).toISOString()}&to=${new Date(`${to}T23:59:59`).toISOString()}`}
          onClick={(e) => {
            // Скачивание идёт с токеном в заголовке, поэтому качаем через fetch.
            e.preventDefault();
            void downloadCsv(
              `/api/admin/reports/export.csv?kind=purchases&from=${new Date(from).toISOString()}&to=${new Date(`${to}T23:59:59`).toISOString()}`,
              `покупки-${from}.csv`,
            );
          }}
        >
          Скачать покупки CSV
        </a>
      </div>

      {error && <div className="notice err">{error}</div>}
      {loading && <p className="subtitle">Считаем…</p>}

      {data && (
        <>
          <h2>За период</h2>
          <div className="grid cols-4">
            <Stat label="Начислено (прошло холд)" value={data.period.accruedBonuses} />
            <Stat label="Ушло в холд" value={data.period.heldBonuses} />
            <Stat label="Потрачено в лавке" value={data.period.spentBonuses} />
            <Stat label="Отменено и возвращено" value={data.period.cancelledBonuses + data.period.clawedBackBonuses} />
          </div>

          <h2>Остаток обязательств</h2>
          <div className="grid cols-4">
            <Stat label="Всего должны ученикам" value={data.liability.total} />
            <Stat label="Доступно к трате" value={data.liability.available} />
            <Stat label="В холде" value={data.liability.hold} />
            <Stat label="На подтверждении" value={data.liability.pending} />
          </div>

          {data.liability.checksum !== 0 && (
            <div className="notice err">
              Леджер не сходится: сумма всех счетов {fmt(data.liability.checksum)} вместо нуля. Это баг, а не
              особенность данных — нужен разбор до следующей выгрузки.
            </div>
          )}
          {data.liability.usersInDebt > 0 && (
            <div className="notice warn">
              У {data.liability.usersInDebt} ученик(ов) отрицательный баланс на общую сумму {fmt(data.liability.debt)} —
              последствие возвратов после траты бонусов.
            </div>
          )}

          <h2>Покупки по товарам</h2>
          <div className="card">
            <table>
              <thead>
                <tr>
                  <th>Товар</th>
                  <th className="num">Покупок</th>
                  <th className="num">Бонусов потрачено</th>
                </tr>
              </thead>
              <tbody>
                {data.purchases.items.map((r) => (
                  <tr key={r.product_title}>
                    <td>{r.product_title}</td>
                    <td className="num">{r.purchases}</td>
                    <td className="num">{fmt(r.bonuses_spent)}</td>
                  </tr>
                ))}
                {data.purchases.items.length === 0 && (
                  <tr>
                    <td colSpan={3} className="muted">За период покупок не было</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="stat">
      <div className="v">{fmt(value)}</div>
      <div className="l">{label}</div>
    </div>
  );
}

async function downloadCsv(url: string, filename: string): Promise<void> {
  const res = await fetch(url, { headers: auth.token ? { authorization: `Bearer ${auth.token}` } : {} });
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ─────────────────────────── Контент (ТЗ §7.4) ───────────────────────────

export function ContentPage() {
  const { data, loading, error, reload } = useAsync(() => api.content(), []);

  return (
    <>
      <h1>Тексты и вопросы</h1>
      <p className="subtitle">Страницы, сообщения об ошибках и раздел вопросов-ответов.</p>
      {error && <div className="notice err">{error}</div>}
      {loading && <p className="subtitle">Загружаем…</p>}
      {(data?.items ?? []).map((raw) => (
        <ContentEditor key={String(raw.key)} block={raw as ContentRow} onSaved={reload} />
      ))}
    </>
  );
}

type ContentRow = {
  key: string;
  kind: 'TEXT' | 'MARKDOWN' | 'HTML' | 'FAQ';
  title: string;
  body: string;
  items: { question: string; answer: string }[];
  is_published: boolean;
  sort_order: number;
};

function ContentEditor({ block, onSaved }: { block: ContentRow; onSaved: () => void }) {
  const [title, setTitle] = useState(block.title);
  const [body, setBody] = useState(block.body);
  const [items, setItems] = useState(block.items ?? []);
  const [published, setPublished] = useState(block.is_published);

  const save = useAction(async () => {
    await api.saveContent(block.key, {
      kind: block.kind,
      title,
      body,
      items,
      isPublished: published,
      sortOrder: block.sort_order,
    });
    onSaved();
  });

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="row between">
        <strong>{block.key}</strong>
        <label className="row" style={{ gap: 6, marginBottom: 0 }}>
          <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} />
          Показывать
        </label>
      </div>
      {save.error && <div className="notice err">{save.error}</div>}
      <div className="field" style={{ marginTop: 12 }}>
        <label>Заголовок</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>

      {block.kind === 'FAQ' ? (
        <>
          {items.map((item, i) => (
            <div key={i} className="grid cols-2" style={{ marginBottom: 8 }}>
              <input
                value={item.question}
                placeholder="Вопрос"
                onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, question: e.target.value } : x)))}
              />
              <div className="row">
                <input
                  value={item.answer}
                  placeholder="Ответ"
                  onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, answer: e.target.value } : x)))}
                />
                <button className="secondary small" onClick={() => setItems(items.filter((_, j) => j !== i))}>
                  ×
                </button>
              </div>
            </div>
          ))}
          <button className="secondary small" onClick={() => setItems([...items, { question: '', answer: '' }])}>
            Добавить вопрос
          </button>
        </>
      ) : (
        <div className="field">
          <label>Текст</label>
          <textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} />
        </div>
      )}

      <button style={{ marginTop: 12 }} disabled={save.busy} onClick={() => void save.run()}>
        {save.busy ? 'Сохраняем…' : 'Сохранить'}
      </button>
      {save.ok && <span className="badge on" style={{ marginLeft: 10 }}>сохранено</span>}
    </div>
  );
}

// ─────────────────────────── Эксплуатация ───────────────────────────

export function OpsPage() {
  const quarantine = useAsync(() => api.quarantine(), []);
  const sync = useAsync(() => api.syncStatus(), []);
  const auditLog = useAsync(() => api.audit(), []);

  const replay = useAction(async () => {
    await api.replay();
    quarantine.reload();
  });
  const resolve = useAction(async (orderId: string) => {
    await api.resolveQuarantine(orderId);
    quarantine.reload();
  });

  return (
    <>
      <h1>Эксплуатация</h1>
      <p className="subtitle">Связь с основной платформой, спорные заказы и журнал действий.</p>

      <h2>Синхронизация с базой реферальной программы</h2>
      <div className="card">
        {sync.data ? (
          <div className="grid cols-4">
            <Stat2 label="Источник" value={String(sync.data.source ?? '—')} />
            <Stat2 label="Последний прогон" value={fmtDate(sync.data.last_run_at as string)} />
            <Stat2 label="Заказов увидено" value={String(sync.data.orders_seen ?? 0)} />
            <Stat2 label="Опубликовано" value={String(sync.data.orders_published ?? 0)} />
          </div>
        ) : (
          <p className="subtitle" style={{ margin: 0 }}>Загружаем…</p>
        )}
        {sync.data?.last_error ? <div className="notice err">{String(sync.data.last_error)}</div> : null}
      </div>

      <h2>Заказы, требующие разбора</h2>
      <p className="subtitle" style={{ marginTop: -4 }}>
        Сюда попадают заказы с нераспознанным типом. Начислить по ним молча нельзя — сначала нужно решение.
      </p>
      <div className="card">
        <div className="row" style={{ marginBottom: 12 }}>
          <button className="secondary small" disabled={replay.busy} onClick={() => void replay.run()}>
            {replay.busy ? 'Перепроигрываем…' : 'Перепроиграть отказы по текущим правилам'}
          </button>
          {replay.ok && <span className="badge on">готово</span>}
        </div>
        {replay.error && <div className="notice err">{replay.error}</div>}
        <table>
          <thead>
            <tr>
              <th>Заказ</th>
              <th>Причина</th>
              <th>Тип</th>
              <th>Когда</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(quarantine.data?.items ?? []).map((raw) => {
              const r = raw as Record<string, string | null>;
              return (
                <tr key={String(r.external_order_id)}>
                  <td>{String(r.external_order_id)}</td>
                  <td>{String(r.reason)}</td>
                  <td>{r.order_type ?? '—'}</td>
                  <td>{fmtDate(r.created_at)}</td>
                  <td>
                    <button
                      className="secondary small"
                      disabled={resolve.busy}
                      onClick={() => void resolve.run(String(r.external_order_id))}
                    >
                      Отметить разобранным
                    </button>
                  </td>
                </tr>
              );
            })}
            {(quarantine.data?.items.length ?? 0) === 0 && (
              <tr>
                <td colSpan={5} className="muted">Всё чисто</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <h2>Журнал действий</h2>
      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Когда</th>
              <th>Кто</th>
              <th>Действие</th>
              <th>Объект</th>
              <th>Причина</th>
            </tr>
          </thead>
          <tbody>
            {(auditLog.data?.items ?? []).map((raw) => {
              const r = raw as Record<string, string | number | null>;
              return (
                <tr key={String(r.id)}>
                  <td>{fmtDate(r.created_at as string)}</td>
                  <td>{String(r.actor_name)}</td>
                  <td>{String(r.action)}</td>
                  <td>
                    {String(r.entity_type)}
                    {r.entity_id ? ` · ${String(r.entity_id)}` : ''}
                  </td>
                  <td>{(r.reason as string) ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Stat2({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat">
      <div className="v" style={{ fontSize: 16 }}>{value}</div>
      <div className="l">{label}</div>
    </div>
  );
}
